import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute } from '@tanstack/react-router';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { Check } from 'lucide-react';
import { type ChangeEvent, type ChangeEventHandler, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import slug from 'slug';
import * as z from 'zod';
import { checkSlug } from '@/api/organizations';
import { useCreateOrganization } from '@/api/organizations/queries';
import { PageContainer } from '@/components/layout/page-container';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

export const Route = createFileRoute('/_authenticated/_organization/organization/create')({
  component: CreateOrganization,
});

export const validateSlug = () =>
  AwesomeDebouncePromise(async (value: string) => {
    if (!value) return false;
    const result = await checkSlug(value);
    return result.status;
  }, 500);

const createOrganizationSchema = z.object({
  name: z
    .string()
    .min(2, 'Organization name must be at least 2 characters')
    .max(100, 'Organization name must be less than 100 characters'),
  slug: z
    .string()
    .min(2, 'Organization slug must be at least 2 characters')
    .max(50, 'Organization slug must be less than 50 characters')
    .regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens')
    .refine(validateSlug(), 'This slug is already taken'),
});

type CreateOrganizationFormData = z.infer<typeof createOrganizationSchema>;

function CreateOrganization() {
  const createOrganizationMutation = useCreateOrganization();

  const form = useForm<CreateOrganizationFormData>({
    resolver: zodResolver(createOrganizationSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      slug: '',
    },
  });

  const generateSlug = useCallback(
    (onChange: ChangeEventHandler<HTMLInputElement>) => (e: ChangeEvent<HTMLInputElement>) => {
      onChange(e);
      const { value } = e.target;
      form.setValue('slug', slug(value), {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      });
    },
    [form],
  );

  const onSlugChange = useCallback(
    (onChange: ChangeEventHandler<HTMLInputElement>) => (e: ChangeEvent<HTMLInputElement>) => {
      e.target.value = slug(e.target.value, { trim: false });
      onChange(e);
    },
    [],
  );

  const onSubmit = async (data: CreateOrganizationFormData) => {
    return createOrganizationMutation.mutateAsync(data);
  };

  return (
    <PageContainer>
      <PageHeader
        title="Create Organization"
        description="Create a new organization to manage your team and projects"
      />
      <Card>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field: { onChange, ...field } }) => (
                  <FormItem>
                    <FormLabel>Organization Name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="text"
                        placeholder="Acme Inc."
                        disabled={form.formState.isSubmitting}
                        onChange={generateSlug(onChange)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="slug"
                render={({
                  field: { onChange, ...field },
                  fieldState: { isDirty, error, isValidating },
                }) => {
                  return (
                    <FormItem>
                      <FormLabel>Organization Slug</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            {...field}
                            type="text"
                            placeholder="acme-inc"
                            disabled={form.formState.isSubmitting}
                            onChange={onSlugChange(onChange)}
                            className="pr-10"
                          />
                          {!isValidating && !error && isDirty && (
                            <Check className="pointer-events-none absolute right-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-green-600" />
                          )}
                        </div>
                      </FormControl>
                      <FormDescription>
                        A unique identifier for your organization (lowercase letters, numbers, and
                        hyphens only)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <div className="flex gap-4">
                <Button
                  type="submit"
                  disabled={form.formState.isSubmitting || !form.formState.isValid}
                >
                  {form.formState.isSubmitting ? 'Creating...' : 'Create Organization'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </PageContainer>
  );
}
